import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"
import { useTranslation } from "react-i18next"

import { cn } from "@/lib/utils"

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/80  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props} />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

// [AUDIT-2026-10-20 DIALOG-WIDTH] A screen asks for its width with a bare
// `max-w-4xl`, but the base below sets `sm:max-w-lg`, and at 640px and up the
// media-query class wins: every wide dialog (the finance profile with its
// 12-month table, the student form, the schedule editor, ~30 in all) was
// squeezed to 512px on a desktop and scrolled sideways. A bare max-w-* is the
// desktop width, so it is applied at sm: (twMerge then drops sm:max-w-lg);
// below 640px the dialog stays the full-screen sheet.
export const desktopWidth = (className = "") =>
  String(className || "")
    .split(/\s+/)
    .filter(Boolean)
    .map((c) => (/^max-w-/.test(c) ? `sm:${c}` : c))
    .join(" ")

// `hideClose` is for a dialog that draws its own close button (the profile
// drawers put a white one on their coloured header); two in one corner overlap.
const DialogContent = React.forwardRef(({ className, children, hideClose = false, ...props }, ref) => {
  const { t } = useTranslation()
  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          // Mobile (< 640px): full-screen sheet — fills the viewport, scrolls
          // its own content, no rounded corners. Desktop (sm+): centred modal
          // with max-w-lg and rounded corners (the original behaviour).
          "fixed inset-0 z-50 flex flex-col gap-4 border bg-background p-4 shadow-lg duration-200 overflow-y-auto",
          "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
          "sm:inset-auto sm:left-[50%] sm:top-[50%] sm:translate-x-[-50%] sm:translate-y-[-50%] sm:grid sm:w-full sm:max-w-lg sm:rounded-lg sm:p-6 sm:max-h-[90vh]",
          "sm:data-[state=closed]:zoom-out-95 sm:data-[state=open]:zoom-in-95 sm:data-[state=closed]:slide-out-to-left-1/2 sm:data-[state=closed]:slide-out-to-top-[48%] sm:data-[state=open]:slide-in-from-left-1/2 sm:data-[state=open]:slide-in-from-top-[48%]",
          desktopWidth(className)
        )}
        {...props}>
        {children}
        {/* The END corner (left in Arabic) and a title at the START: with
            right-4 and sm:text-left, an Arabic title sat left and the close
            button covered whatever started a right-aligned title. */}
        {!hideClose && (
          <DialogPrimitive.Close
            className="absolute end-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground p-2 min-w-[44px] min-h-[44px] flex items-center justify-center">
            <X className="h-5 w-5 sm:h-4 sm:w-4" />
            <span className="sr-only">{t("close")}</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  )
})
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col space-y-1.5 text-center sm:text-start", className)}
    {...props} />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className)}
    {...props} />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold leading-none tracking-tight", className)}
    {...props} />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props} />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
